import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';

// Default values
const DEFAULT_INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const DEFAULT_WARNING_TIMEOUT = 30 * 1000; //  30 second before timeout

const useInactivityTimer = (inactivityTimeout = DEFAULT_INACTIVITY_TIMEOUT, warningTimeout = DEFAULT_WARNING_TIMEOUT) => {
    const navigate = useNavigate();
    const { logoutUser } = useUser();
    const timeoutRef = useRef(null);
    const warningTimeoutRef = useRef(null);
    const [showWarning, setShowWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(Math.floor(warningTimeout / 1000));

    const resetTimer = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (warningTimeoutRef.current) {
            clearTimeout(warningTimeoutRef.current);
        }
        setShowWarning(false);
        
        // Set warning timeout
        warningTimeoutRef.current = setTimeout(() => {
            setShowWarning(true);
            // Start countdown
            let countdown = Math.floor(warningTimeout / 1000);
            const countdownInterval = setInterval(() => {
                countdown--;
                setTimeLeft(countdown);
                if (countdown <= 0) {
                    clearInterval(countdownInterval);
                }
            }, 1000);
        }, inactivityTimeout - warningTimeout);

        // Set logout timeout
        timeoutRef.current = setTimeout(() => {
            logoutUser();
            navigate('/login');
        }, inactivityTimeout);
    };

    const handleStayLoggedIn = () => {
        resetTimer();
    };

    useEffect(() => {
        // Events to track user activity
        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click'
        ];

        // Add event listeners
        events.forEach(event => {
            window.addEventListener(event, resetTimer);
        });

        // Initial timer setup
        resetTimer();

        // Cleanup function
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (warningTimeoutRef.current) {
                clearTimeout(warningTimeoutRef.current);
            }
            events.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, [navigate, logoutUser, inactivityTimeout, warningTimeout]);

    return {
        showWarning,
        timeLeft,
        handleStayLoggedIn
    };
};

export default useInactivityTimer; 